import { getCustomerPaymentCard } from "../StripeUtils"
import { SUBSCRIPTION_STATUS } from "../../services/stripe/stripe.const"
import { getPurchasingStripe } from "../../utils/purchasing-plan"
import { getExpensesStripe } from "../../utils/expenses-plan"
import { getEquipmentStripe } from "../../utils/equipment-plan"
import { goToParams } from "../History"
import { toast } from "react-toastify"
import { getRefId } from "../../utils/utils"

/**
 * Section ids of OrgSubscriptions.js - an issue points at the section where the admin
 * can actually fix it, so the red dot and the deep-link land in the same place.
 */
export const SUBSCRIPTION_SECTION = {
    SUBSCRIPTION: "subscription",
    BILLING: "billing",
    USAGE: "usage",
    AI_PURCHASING: "ai-purchasing",
    AI_EXPENSES: "ai-expenses",
    // Matches TRIAL_PRODUCT_SECTION in the API's stripe.utils.js (trial-ending email link).
    EQUIPMENT: "equipment",
    AI_AGENTS: "ai-agents",
    POWER_AI: "power-ai",
}

// Params of the overlays that can sit on top of (or instead of) an org: an open mission
// and the create-mission flow. App.js won't mount Org.js while a mission is open, so
// these have to go before the org can show its subscriptions panel.
const OVERLAY_PARAMS =
    "mission,phase,mc,mc-org,mc-program,create-mission,mc-org-goal,mc-prop,create-proposal,mc-departments,mc-offices,mc-client,mc-title,mc-type,mc-template"

/**
 * Navigates to an org's subscriptions panel from outside Org.js (mission-level upgrade
 * prompts, the Board's cost tracker). Inside Org.js use the `setShowSubscriptions`
 * setter instead - Org.js seeds the panel from the `org-sub` param on mount, and its
 * history listener picks it up if it's already mounted.
 */
export const openOrgSubscriptions = (orgId, section = SUBSCRIPTION_SECTION.SUBSCRIPTION, extraParams = []) => {
    // One history entry, so the back button returns to the mission. It also reaches an Org that
    // is already open (its own history never hears a plain push), and drops its settings panel.
    goToParams({
        remove: `${OVERLAY_PARAMS},org-cfg`,
        add: [{ param: "org", value: orgId }, { param: "org-sub", value: section }, ...extraParams],
    })
}

/**
 * Set alongside `org-sub` to open the subscriptions panel with the Pro trial checkout
 * already up. SubscriptionOverview reads it once on mount and drops it.
 */
export const PRO_TRIAL_PARAM = "org-sub-trial"

/**
 * Every "Start a free Pro trial" button outside the Pro tile. The trial takes a card -
 * it comes with free AI credits, so it is for people prepared to pay - which means it
 * can't start in one click from wherever the button is. This opens the subscriptions
 * panel with the trial checkout on top. Org admins only; anyone else is told to ask one.
 *
 * @param {object} org
 * @param {object} app
 */
export const openProTrial = (org, app) => {
    const meInOrg = org?.people?.find((p) => getRefId(p) === app?.state?.person?._id)

    if (meInOrg?.permission !== 2) {
        toast("Please ask an organization admin to start the Pro trial.", { autoClose: 6000 })
        return
    }

    openOrgSubscriptions(org._id, SUBSCRIPTION_SECTION.SUBSCRIPTION, [{ param: PRO_TRIAL_PARAM, value: "1" }])
}

// Stripe statuses that mean "money didn't move" - a failed charge, or a subscription
// that never got past its first payment (incomplete). Anything else is either healthy
// (active/trialing), deliberately ended (canceled) or never started (not_setup).
const FAILED_STATUSES = [
    SUBSCRIPTION_STATUS.PAST_DUE,
    SUBSCRIPTION_STATUS.UNPAID,
    SUBSCRIPTION_STATUS.INCOMPLETE,
    SUBSCRIPTION_STATUS.INCOMPLETE_EXPIRED,
]

export const CARD_STATE = {
    EXPIRED: "expired",
    EXPIRING: "expiring",
}

/**
 * A card is usable through the last day of its expiry month, so "expired" means the
 * month has fully passed and "expiring" means we're inside it - Stripe will fail the
 * next renewal either way, so both are worth a dot.
 *
 * @param card {Object} Stripe card (exp_month / exp_year)
 * @return {String|null} CARD_STATE or null when fine / unknown
 */
export function getCardState(card) {
    if (!card?.exp_month || !card?.exp_year) return null

    const now = new Date()
    const nowYear = now.getFullYear()
    const nowMonth = now.getMonth() + 1

    if (card.exp_year < nowYear || (card.exp_year === nowYear && card.exp_month < nowMonth)) {
        return CARD_STATE.EXPIRED
    }
    if (card.exp_year === nowYear && card.exp_month === nowMonth) {
        return CARD_STATE.EXPIRING
    }

    return null
}

/**
 * Everything wrong with an org's subscriptions, in the order an admin should deal with
 * them. Derived from the org document (subscription statuses) and, when available, the
 * Stripe customer (card on file) - `customer` is optional so the header can already show
 * status-based issues before the customer request resolves.
 *
 * @param orgData {Object}
 * @param [customer] {Object} Stripe customer, expanded with invoice_settings.default_payment_method
 * @return {Array<{id: String, section: String, message: String}>}
 */
export function getSubscriptionIssues(orgData, customer) {
    const issues = []
    if (!orgData) return issues

    const proStatus = orgData.stripe?.subscriptionStatus
    const hasProSubscription =
        !!proStatus && ![SUBSCRIPTION_STATUS.NOT_SETUP, SUBSCRIPTION_STATUS.CANCELLED].includes(proStatus)

    if (FAILED_STATUSES.includes(proStatus)) {
        issues.push({
            id: "pro-payment-failed",
            section: SUBSCRIPTION_SECTION.BILLING,
            message: "Your Pro payment failed - update your card to avoid losing access.",
        })
    }

    if (customer && hasProSubscription) {
        const card = getCustomerPaymentCard(customer)
        const cardState = getCardState(card)

        if (cardState === CARD_STATE.EXPIRED) {
            issues.push({
                id: "card-expired",
                section: SUBSCRIPTION_SECTION.BILLING,
                message: "The card on file has expired.",
            })
        } else if (cardState === CARD_STATE.EXPIRING) {
            issues.push({
                id: "card-expiring",
                section: SUBSCRIPTION_SECTION.BILLING,
                message: "The card on file expires this month.",
            })
        } else if (!card && proStatus === SUBSCRIPTION_STATUS.ACTIVE) {
            // Only an active paid subscription - it will fail at the next renewal. A trial
            // left with no card (started before cards were required, or its card removed)
            // just lapses, and the Pro tile already says so.
            issues.push({
                id: "card-missing",
                section: SUBSCRIPTION_SECTION.BILLING,
                message: "There's no card on file for your Pro subscription.",
            })
        }
    }

    if (FAILED_STATUSES.includes(getPurchasingStripe(orgData).subscriptionStatus)) {
        issues.push({
            id: "purchasing-payment-failed",
            section: SUBSCRIPTION_SECTION.AI_PURCHASING,
            message: "Your AI Purchasing payment failed - update your card to keep the add-on running.",
        })
    }

    if (FAILED_STATUSES.includes(getExpensesStripe(orgData).subscriptionStatus)) {
        issues.push({
            id: "expenses-payment-failed",
            section: SUBSCRIPTION_SECTION.AI_EXPENSES,
            message: "Your AI Expenses payment failed - update your card to keep the add-on running.",
        })
    }

    if (FAILED_STATUSES.includes(getEquipmentStripe(orgData).subscriptionStatus)) {
        issues.push({
            id: "equipment-payment-failed",
            section: SUBSCRIPTION_SECTION.EQUIPMENT,
            message: "Your Equipment payment failed - update your card to keep the add-on running.",
        })
    }

    return issues
}
